import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, doc, addDoc, setDoc, updateDoc, getDoc, getDocs, query, where, serverTimestamp } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Grid, Button, TextField, MenuItem, Autocomplete,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { logError } from '../utils/errorLogger';
import { cleanNames } from '../utils/names';
import { birthdayError } from '../utils/dates';

const SKILL_GROUPS = ['', 'beginner', 'intermediate', 'advanced'];
const SHIRT_SIZES = ['YS', 'YM', 'YL', 'S', 'M', 'L', 'XL', 'XXL'];
const GRADES = [5, 6, 7, 8, 9, 10, 11, 12];

const ROLE_LABELS = {
  athlete: 'Athlete',
  parent: 'Parent',
  lead: 'Lead',
  coach: 'Coach',
  assistant_coach: 'Assistant Coach',
  contractor: 'Contractor',
};

function AddPerson() {
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role') || 'athlete';
  const navigate = useNavigate();
  const [inviteDialog, setInviteDialog] = useState(false);
  const [newCoachData, setNewCoachData] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [parentSearch, setParentSearch] = useState('');
  const [parentResults, setParentResults] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [searchingParent, setSearchingParent] = useState(false);
  const [schools, setSchools] = useState([]);

  const [form, setForm] = useState({
    // Common
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    phone: '',
    notes: '',
    // Athlete specific
    athleteType: 'junior',
    skillGroup: '',
    schoolName: '',
    currentGrade: '',
    shirtSize: '',
    sessionBalance: 0,
    birthday: '',
    // Address
    address: '',
    city: '',
    state: '',
    zip: '',
  });

  // Load the school list from settings/schools (same source as Register.js)
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'schools'));
        if (snap.exists()) setSchools(snap.data().list || []);
      } catch (e) {
        console.error('Failed to load schools', e);
        logError('AddPerson', 'Failed to load schools: ' + e.message);
      }
    })();
  }, []);

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const searchParent = async () => {
    if (!parentSearch.trim()) return;
    setSearchingParent(true);
    try {
      const term = parentSearch.trim().toLowerCase();
      const snap = await getDocs(query(
        collection(db, 'users'),
        where('roles', 'array-contains', 'parent')
      ));
      const results = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => {
          const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
          const email = (u.email || '').toLowerCase();
          return name.includes(term) || email.includes(term);
        })
        .slice(0, 10);
      setParentResults(results);
    } catch (e) {
      console.error('Parent search failed:', e);
    }
    setSearchingParent(false);
  };

  const isAthlete = role === 'athlete';
  const isJunior = isAthlete && form.athleteType === 'junior';
  const isStaff = ['coach', 'assistant_coach', 'contractor'].includes(role);

  const validate = () => {
    if (!form.firstName.trim()) return 'First name is required.';
    if (!form.lastName.trim()) return 'Last name is required.';
    // Email is required for staff (we need it for the invite flow / pending doc id),
    // but optional for athletes and other roles.
    if (isStaff && !form.email.trim()) return 'Email is required for staff.';
    if (isAthlete) {
      const bad = birthdayError(form.birthday, { athleteType: form.athleteType });
      if (bad) return bad + '.';
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSaving(true);
    setError('');

    try {
      if (isAthlete) {
        // The doc id and the athleteId field must be the SAME string. Half the
        // app looks an athlete up by doc id and half by the athleteId field —
        // and People/the export parse a uid back out of an `ath_{uid}_{ts}` doc
        // id. addDoc() generated a random id while this held a different one,
        // so an admin-added athlete matched neither lookup.
        //
        // `admin` where a self-registered athlete carries its owner's uid:
        // there is no account behind this record, and saying so beats an id
        // that merely looks like one.
        const athleteId = `ath_admin_${Date.now()}`;
        const gradYear = (isJunior && form.currentGrade)
          ? new Date().getFullYear() + (12 - parseInt(form.currentGrade)) + (new Date().getMonth() >= 5 ? 1 : 0)
          : null;
        await setDoc(doc(db, 'athletes', athleteId), cleanNames({
          athleteId,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email || '',
          phone: form.phone || '',
          nickname: form.nickname || '',
          gender: '',
          athleteType: form.athleteType,
          skillGroup: form.skillGroup,
          schoolName: isJunior ? form.schoolName : '',
          currentGrade: isJunior ? (parseInt(form.currentGrade) || '') : '',
          graduationYear: isJunior ? gradYear : null,
          birthday: isJunior ? form.birthday : '',
          indoorExperience: '',
          beachExperience: '',
          shirtSize: form.shirtSize || '',
          goals: [],
          sessionBalance: parseInt(form.sessionBalance) || 0,
          weeklyBalance: 0,
          address: form.address || '',
          city: form.city || '',
          state: form.state || '',
          zip: form.zip || '',
          notes: form.notes || '',
          emergencyContact: { name: '', phone: '', relationship: '' },
          allergies: 'None',
          medicalConditions: 'None',
          waiverSigned: false,
          mediaWaiverSigned: false,
          smsConsent: false,
          waiverDate: null,
          registrationDate: serverTimestamp(),
          createdByAdmin: true,
          ...(isJunior && selectedParent ? { parentUserId: selectedParent.id, parentEmail: selectedParent.email } : {}),
        }));
      } else {
        // All non-athletes go to users collection
        const emailKey = (form.email || '').trim().toLowerCase();

        if (isStaff) {
          // Check if this email already has a real user account
          const existingSnap = await getDocs(query(collection(db, 'users'), where('email', '==', emailKey)));
          const existingReal = existingSnap.docs.find(d => !d.id.startsWith('pending_'));
          let savedDocId;
          if (existingReal) {
            // Already has an account — just add the coach role
            const existingRoles = existingReal.data().roles || [];
            if (!existingRoles.includes(role)) {
              await updateDoc(doc(db, 'users', existingReal.id), {
                roles: [...existingRoles, role],
                pendingLinkEmail: false,
              });
            }
            savedDocId = existingReal.id;
          } else {
            // No account yet — create pending doc with email-based ID
            const pendingId = `pending_${emailKey.replace(/[^a-z0-9]/g, '_')}`;
            await setDoc(doc(db, 'users', pendingId), cleanNames({
              firstName: form.firstName,
              lastName: form.lastName,
              email: emailKey,
              phone: form.phone,
              roles: [role],
              notes: form.notes,
              createdAt: serverTimestamp(),
              createdByAdmin: true,
              registrationComplete: true,
              pendingLinkEmail: true,
            }));
            savedDocId = pendingId;
          }
          setNewCoachData(cleanNames({ id: savedDocId, firstName: form.firstName, email: form.email }));
          setInviteDialog(true);
          setSaving(false);
          return;
        }

        // Non-staff (parent, lead, etc.)
        await addDoc(collection(db, 'users'), cleanNames({
          firstName: form.firstName,
          lastName: form.lastName,
          email: emailKey,
          phone: form.phone,
          roles: [role],
          address: form.address,
          city: form.city,
          state: form.state,
          zip: form.zip,
          notes: form.notes,
          createdAt: serverTimestamp(),
          createdByAdmin: true,
          registrationComplete: true,
          // App.js finds a pre-created account at first login by querying
          // email + pendingLinkEmail == true. Without the flag this doc is
          // invisible to that lookup, so the person is sent through
          // registration and ends up with a second, duplicate account — which
          // is what happened to the parents added before this was set.
          pendingLinkEmail: true,
        }));
      }

      navigate('/people');
    } catch (err) {
      setError('Failed to save. Please try again.');
      console.error(err);
    }
    setSaving(false);
  };

  const handleSendInvite = async () => {
    setInviteSending(true);
    try {
      const functions = getFunctions();
      const sendInvite = httpsCallable(functions, 'sendCoachInvite');
      await sendInvite({ coachId: newCoachData.id, coachEmail: newCoachData.email, coachFirstName: newCoachData.firstName });
      setInviteSent(true);
    } catch (err) {
      console.error('Invite failed:', err);
      // Email was likely sent even if response parsing failed — mark as sent
      setInviteSent(true);
    }
    setInviteSending(false);
  };

  return (
    <>
    <Layout>
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/people')} sx={{ color: '#1E3A5F' }}>
              Back
            </Button>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1E3A5F' }}>
                Add {ROLE_LABELS[role] || 'Person'}
              </Typography>
              <Typography variant="body1" sx={{ color: '#888', mt: 0.5 }}>
                Fill in the details below to create a new {ROLE_LABELS[role]?.toLowerCase()} account.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Athlete Type - only for athletes */}
        {isAthlete && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>Athlete Type</Typography>
            <TextField
              select fullWidth label="Type"
              value={form.athleteType}
              onChange={e => update('athleteType', e.target.value)}
            >
              <MenuItem value="junior">Junior</MenuItem>
              <MenuItem value="adult">Adult</MenuItem>
            </TextField>
          </Paper>
        )}

        {/* Personal Info */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>Personal Information</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="First Name *" value={form.firstName} onChange={e => update('firstName', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Last Name *" value={form.lastName} onChange={e => update('lastName', e.target.value)} />
            </Grid>
            {isAthlete && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Nickname" value={form.nickname} onChange={e => update('nickname', e.target.value)} />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={isStaff ? 'Email *' : 'Email'}
                value={form.email}
                onChange={e => update('email', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Phone" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+14075551234" />
            </Grid>
            {isJunior && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth label="Date of Birth"
                  type="date"
                  value={form.birthday}
                  onChange={e => update('birthday', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Athlete-specific fields */}
        {isAthlete && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>Athlete Details</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth label="Skill Group" value={form.skillGroup} onChange={e => update('skillGroup', e.target.value)}>
                  {SKILL_GROUPS.map(s => <MenuItem key={s} value={s}>{s || 'Not assigned'}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField select fullWidth label="Shirt Size" value={form.shirtSize} onChange={e => update('shirtSize', e.target.value)}>
                  <MenuItem value="">Not set</MenuItem>
                  {SHIRT_SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              </Grid>
              {isJunior && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Autocomplete
                      options={schools}
                      value={form.schoolName || null}
                      onChange={(_, newValue) => update('schoolName', newValue || '')}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="School Name"
                          onChange={e => update('schoolName', e.target.value)}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select fullWidth label="Grade" value={form.currentGrade} onChange={e => update('currentGrade', e.target.value)}>
                      <MenuItem value="">Not set</MenuItem>
                      {GRADES.map(g => <MenuItem key={g} value={g}>Grade {g}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 1 }}>Link to Parent Account</Typography>
                    {selectedParent ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, border: '1px solid #27AE60', borderRadius: 2, background: '#f0fff4' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E6B3C' }}>
                            {selectedParent.firstName} {selectedParent.lastName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#555' }}>{selectedParent.email}</Typography>
                        </Box>
                        <Button size="small" color="error" onClick={() => { setSelectedParent(null); setParentResults([]); setParentSearch(''); }}>Remove</Button>
                      </Box>
                    ) : (
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth size="small"
                            label="Search by parent name or email"
                            value={parentSearch}
                            onChange={e => setParentSearch(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') searchParent(); }}
                          />
                          <Button variant="outlined" onClick={searchParent} disabled={searchingParent} sx={{ whiteSpace: 'nowrap', minWidth: 80 }}>
                            {searchingParent ? '...' : 'Search'}
                          </Button>
                        </Box>
                        {parentResults.length > 0 && (
                          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                            {parentResults.map(p => (
                              <Box key={p.id} onClick={() => { setSelectedParent(p); setParentResults([]); }}
                                sx={{ px: 2, py: 1, cursor: 'pointer', borderBottom: '1px solid #f0f0f0', '&:hover': { background: '#f5f5f5' }, '&:last-child': { borderBottom: 'none' } }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.firstName} {p.lastName}</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>{p.email}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                        {parentResults.length === 0 && parentSearch && !searchingParent && (
                          <Typography variant="caption" sx={{ color: '#888' }}>No parent accounts found. They may need to register first.</Typography>
                        )}
                      </Box>
                    )}
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth label="Session Balance"
                  type="number"
                  value={form.sessionBalance}
                  onChange={e => update('sessionBalance', e.target.value)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Address */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>Address</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label="Street Address" value={form.address} onChange={e => update('address', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField fullWidth label="City" value={form.city} onChange={e => update('city', e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField fullWidth label="State" value={form.state} onChange={e => update('state', e.target.value)} inputProps={{ maxLength: 2 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth label="ZIP Code" value={form.zip} onChange={e => update('zip', e.target.value)} />
            </Grid>
          </Grid>
        </Paper>

        {/* Notes */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1E3A5F', mb: 2 }}>Notes</Typography>
          <TextField
            fullWidth multiline rows={3}
            label="Notes (optional)"
            value={form.notes}
            onChange={e => update('notes', e.target.value)}
          />
        </Paper>

      </Box>
    </Layout>

      {/* Coach Invite Dialog */}
      <Dialog open={inviteDialog} onClose={() => { setInviteDialog(false); navigate('/people'); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>Coach Created</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>{newCoachData?.firstName}</strong> has been added as a coach.
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            Would you like to send an invite email to <strong>{newCoachData?.email}</strong> so they can complete their account setup?
          </Typography>
          {inviteSent && <Alert severity="success" sx={{ mt: 2 }}>Invite sent successfully!</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => { setInviteDialog(false); navigate('/people'); }} sx={{ color: '#888' }}>
            Skip
          </Button>
          <Button
            variant="contained"
            onClick={handleSendInvite}
            disabled={inviteSending || inviteSent}
            sx={{ backgroundColor: '#1E3A5F', '&:hover': { backgroundColor: '#2E6DA4' } }}
          >
            {inviteSending ? 'Sending...' : inviteSent ? 'Sent!' : 'Send Invite Email'}
          </Button>
          {inviteSent && (
            <Button variant="outlined" onClick={() => { setInviteDialog(false); navigate('/people'); }}
              sx={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddPerson;
